<template>
  <v-container>
    <v-row class="mb-1">
      <v-col>
        <div class="text-h5 font-weight-bold">
          ລາຍງານການວາງບິນ AR ພາກສະໜາມ
        </div>
        <div class="text-body-2 grey--text">
          ໃຜວາງບິນ (ສ້າງ QR) ໃຫ້ລູກຄ້າໃດ ໃນມື້ທີ່ເລືອກ, ແລະ ບິນເຫຼົ່ານັ້ນຈ່າຍແລ້ວ ຫຼື ຍັງ
        </div>
      </v-col>
    </v-row>

    <v-card
      outlined
      class="mb-4"
    >
      <v-card-text>
        <v-row align="center">
          <v-col
            cols="12"
            sm="auto"
          >
            <v-menu
              v-model="dateMenu"
              :close-on-content-click="true"
              min-width="auto"
              offset-y
              transition="scale-transition"
            >
              <template #activator="{ on, attrs }">
                <v-text-field
                  v-model="date"
                  dense
                  hide-details
                  label="ວັນທີວາງບິນ"
                  outlined
                  prepend-inner-icon="mdi-calendar"
                  readonly
                  style="min-width: 170px"
                  v-bind="attrs"
                  v-on="on"
                />
              </template>
              <v-date-picker v-model="date" />
            </v-menu>
          </v-col>
          <v-col
            cols="12"
            sm="3"
          >
            <v-autocomplete
              v-model="userId"
              :items="staffItems"
              :loading="loadingStaff"
              clearable
              dense
              hide-details
              item-text="text"
              item-value="id"
              label="AR ພາກສະໜາມ"
              outlined
            />
          </v-col>
          <v-col
            cols="12"
            sm="2"
          >
            <v-autocomplete
              v-model="districtId"
              :items="districts"
              clearable
              dense
              hide-details
              item-text="name"
              item-value="id"
              label="ເມືອງ"
              outlined
            />
          </v-col>
          <v-col
            cols="12"
            sm="2"
          >
            <v-autocomplete
              v-model="villageId"
              :items="villages"
              :disabled="!districtId"
              clearable
              dense
              hide-details
              item-text="name"
              item-value="id"
              label="ບ້ານ"
              outlined
            />
          </v-col>
          <v-col
            cols="12"
            sm="2"
          >
            <v-select
              v-model="status"
              :items="statusItems"
              dense
              hide-details
              item-text="text"
              item-value="value"
              label="ສະຖານະບິນ"
              outlined
            />
          </v-col>
        </v-row>
        <v-row>
          <v-spacer />
          <v-col cols="auto">
            <v-btn
              color="primary"
              depressed
              :loading="loading"
              @click="fetchData"
            >
              <v-icon
                left
                small
              >
                mdi-magnify
              </v-icon>
              ຄົ້ນຫາ
            </v-btn>
          </v-col>
          <v-col cols="auto">
            <v-btn
              color="success"
              depressed
              :loading="exporting"
              @click="onExport"
            >
              <v-icon
                left
                small
              >
                mdi-microsoft-excel
              </v-icon>
              Export Excel
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <v-row>
      <v-col
        v-for="card in cards"
        :key="card.label"
        cols="6"
        md
      >
        <v-card
          class="rounded-lg"
          outlined
        >
          <v-card-text class="pa-4">
            <div class="text-body-2 grey--text mb-1">
              {{ card.label }}
            </div>
            <div :class="['text-h5 font-weight-bold', card.color]">
              {{ card.value }}
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-card
      class="mt-4"
      outlined
    >
      <v-data-table
        :expanded.sync="expanded"
        :headers="headers"
        :items="rows"
        :items-per-page="-1"
        :loading="loading"
        dense
        hide-default-footer
        item-key="id"
        no-data-text="ບໍ່ມີການວາງບິນໃນມື້ນີ້"
        show-expand
      >
        <template #[`item.index`]="{ index }">
          {{ index + 1 }}
        </template>
        <template #[`item.value`]="{ item }">
          <strong>{{ formatNumber(item.value) }}</strong>
        </template>
        <template #[`item.paid_qr`]="{ item }">
          {{ formatNumber(item.paid_qr) }}
        </template>
        <template #[`item.paid_other`]="{ item }">
          {{ formatNumber(item.paid_other) }}
        </template>
        <template #[`item.unpaid`]="{ item }">
          <span :class="item.unpaid ? 'orange--text text--darken-3' : ''">{{ formatNumber(item.unpaid) }}</span>
        </template>
        <template #[`item.rate`]="{ item }">
          {{ item.rate }}%
        </template>
        <template #expanded-item="{ headers: cols, item }">
          <td
            :colspan="cols.length"
            class="ar-bills-cell"
          >
            <div class="ar-bills">
              <div class="ar-bills-title mb-2">
                <v-icon
                  color="primary"
                  small
                >
                  mdi-subdirectory-arrow-right
                </v-icon>
                ລູກຄ້າທີ່ {{ item.name }} ວາງບິນໃຫ້
                <span class="grey--text">
                  ({{ item.customers }} ລູກຄ້າ, {{ item.bill_count }} ບິນ, ວາງ {{ item.placements }} ຄັ້ງ)
                </span>
              </div>
              <v-simple-table
                class="ar-bills-table"
                dense
              >
                <thead>
                  <tr>
                    <th>ເວລາວາງ</th>
                    <th>ລູກຄ້າ</th>
                    <th>ເບີໂທ</th>
                    <th>ບ້ານ / ເມືອງ</th>
                    <th class="text-right">
                      ຈຳນວນບິນ
                    </th>
                    <th>ບິນທີ່ວາງ</th>
                    <th class="text-right">
                      ມູນຄ່າ
                    </th>
                    <th class="text-right">
                      ຍັງບໍ່ຈ່າຍ
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(row, i) in item.rows"
                    :key="i"
                  >
                    <td class="text-no-wrap">
                      {{ formatTime(row.first_placed_at) }}
                      <div
                        v-if="row.placements > 1"
                        class="grey--text ar-bills-sub"
                      >
                        ວາງ {{ row.placements }} ຄັ້ງ
                      </div>
                    </td>
                    <td>{{ row.customer_name }}</td>
                    <td>{{ row.phone }}</td>
                    <td>
                      {{ row.village_name || '-' }}
                      <div class="grey--text ar-bills-sub">
                        {{ row.district_name }}
                      </div>
                    </td>
                    <td class="text-right font-weight-bold">
                      {{ row.bill_count }}
                    </td>
                    <td>
                      <div
                        v-for="bill in row.bills"
                        :key="bill.id"
                        class="bill-line"
                      >
                        <span>{{ bill.display_id || `ID#${String(bill.id).padStart(5, '0')}` }}</span>
                        <span class="grey--text">{{ formatMonth(bill.bill_date) }}</span>
                        <span>{{ formatNumber(bill.total) }}</span>
                        <v-chip
                          :color="outcomeColor(bill.outcome)"
                          dark
                          x-small
                        >
                          {{ outcomeLabel(bill.outcome) }}
                        </v-chip>
                        <span
                          v-if="bill.paided_at"
                          class="grey--text"
                        >{{ formatTime(bill.paided_at) }}</span>
                      </div>
                    </td>
                    <td class="text-right">
                      {{ formatNumber(row.value) }}
                    </td>
                    <td class="text-right">
                      <span :class="row.unpaid ? 'orange--text text--darken-3' : ''">{{ formatNumber(row.unpaid) }}</span>
                    </td>
                  </tr>
                </tbody>
              </v-simple-table>
            </div>
          </td>
        </template>
        <template
          v-if="rows.length"
          #[`body.append`]
        >
          <tr class="font-weight-bold">
            <td />
            <td>ລວມ</td>
            <td class="text-right">
              {{ formatNumber(summary.customers) }}
            </td>
            <td class="text-right">
              {{ formatNumber(summary.bill_count) }}
            </td>
            <td class="text-right">
              {{ formatNumber(summary.value) }}
            </td>
            <td class="text-right">
              {{ formatNumber(summary.paid_qr) }}
            </td>
            <td class="text-right">
              {{ formatNumber(summary.paid_other) }}
            </td>
            <td class="text-right">
              {{ formatNumber(summary.unpaid) }}
            </td>
            <td class="text-right">
              {{ summary.rate }}%
            </td>
            <td />
          </tr>
        </template>
      </v-data-table>
    </v-card>
  </v-container>
</template>

<script>
const today = () => new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 10);

const emptySummary = () => ({
  staff: 0, placements: 0, customers: 0, bill_count: 0, value: 0, paid_qr: 0, paid_other: 0, unpaid: 0, rate: 0,
});

const OUTCOMES = {
  paid_qr: { label: 'ຈ່າຍແລ້ວ (QR)', color: 'green' },
  paid_other: { label: 'ຈ່າຍແລ້ວ (ອື່ນໆ)', color: 'blue' },
  unpaid: { label: 'ຍັງບໍ່ຈ່າຍ', color: 'orange darken-2' },
};

export default {
  title() {
    return 'Vientiane Waste Co-Dev|AR bill placement';
  },
  data() {
    return {
      date: today(),
      dateMenu: false,
      userId: null,
      districtId: null,
      villageId: null,
      status: null,
      statusItems: [
        { text: 'ທຸກສະຖານະ', value: null },
        { text: 'ຍັງບໍ່ຈ່າຍ', value: 'unpaid' },
        { text: 'ຈ່າຍແລ້ວ', value: 'paid' },
      ],
      staff: [],
      districts: [],
      villages: [],
      loadingStaff: false,
      loading: false,
      exporting: false,
      rows: [],
      summary: emptySummary(),
      expanded: [],
      headers: [
        {
          text: '#', value: 'index', sortable: false, width: 50,
        },
        { text: 'AR', value: 'name' },
        { text: 'ລູກຄ້າ', value: 'customers', align: 'end' },
        { text: 'ບິນ', value: 'bill_count', align: 'end' },
        { text: 'ມູນຄ່າທີ່ວາງ', value: 'value', align: 'end' },
        { text: 'ຈ່າຍຜ່ານ QR', value: 'paid_qr', align: 'end' },
        { text: 'ຈ່າຍວິທີອື່ນ', value: 'paid_other', align: 'end' },
        { text: 'ຍັງບໍ່ຈ່າຍ', value: 'unpaid', align: 'end' },
        { text: '%', value: 'rate', align: 'end' },
        { text: '', value: 'data-table-expand' },
      ],
    };
  },
  computed: {
    cards() {
      return [
        { label: 'AR ທີ່ວາງບິນ', value: this.formatNumber(this.summary.staff), color: '' },
        { label: 'ບິນທີ່ວາງ', value: this.formatNumber(this.summary.bill_count), color: '' },
        { label: 'ມູນຄ່າທີ່ວາງ (LAK)', value: this.formatNumber(this.summary.value), color: '' },
        { label: 'ຈ່າຍແລ້ວ', value: this.formatNumber(this.summary.paid_qr + this.summary.paid_other), color: 'green--text' },
        { label: 'ຍັງບໍ່ຈ່າຍ', value: this.formatNumber(this.summary.unpaid), color: 'orange--text text--darken-3' },
        { label: 'ອັດຕາເກັບໄດ້', value: `${this.summary.rate}%`, color: 'blue--text' },
      ];
    },
    staffItems() {
      return this.staff.map((user) => ({
        id: user.id,
        text: [user.emp_name ? `${user.emp_name} ${user.emp_surname || ''}` : user.name, user.phone]
          .filter(Boolean).join(' - '),
      }));
    },
  },
  watch: {
    async districtId(id) {
      this.villageId = null;
      this.villages = [];
      if (!id) return;
      try {
        const res = await this.$axios.get(`info/district/${id}/village`);
        this.villages = (res.data && res.data.data) || [];
      } catch (_) {
        this.villages = [];
      }
    },
    date() {
      this.fetchData();
    },
  },
  created() {
    this.fetchStaff();
    this.fetchDistricts();
    this.fetchData();
  },
  methods: {
    formatNumber(value) {
      return Intl.NumberFormat().format(value || 0);
    },
    formatTime(value) {
      if (!value) return '-';
      return String(value).replace('T', ' ').slice(11, 16);
    },
    formatMonth(value) {
      if (!value) return '';
      const [y, m] = String(value).split('-');
      return `${m}/${y}`;
    },
    outcomeLabel(outcome) {
      return (OUTCOMES[outcome] || {}).label || outcome;
    },
    outcomeColor(outcome) {
      return (OUTCOMES[outcome] || {}).color || 'grey';
    },
    params() {
      const params = { date: this.date };
      if (this.userId) params.user_id = this.userId;
      if (this.districtId) params.district_id = this.districtId;
      if (this.villageId) params.village_id = this.villageId;
      if (this.status) params.status = this.status;
      return params;
    },
    async fetchStaff() {
      this.loadingStaff = true;
      try {
        const res = await this.$axios.get('user-setting/user', {
          params: { 'roles[]': 'ar_field', per_page: 1000 },
        });
        const data = (res.data && res.data.data) || [];
        this.staff = Array.isArray(data) ? data : data.data || [];
      } catch (_) {
        this.staff = [];
      } finally {
        this.loadingStaff = false;
      }
    },
    async fetchDistricts() {
      try {
        const res = await this.$axios.get('info/district', { params: { province_id: 1 } });
        this.districts = (res.data && res.data.data) || [];
      } catch (_) {
        this.districts = [];
      }
    },
    async fetchData() {
      this.loading = true;
      try {
        const res = await this.$axios.get('v2/report-ar-bill-placement', { params: this.params() });
        const data = (res.data && res.data.data) || {};
        this.rows = data.data || [];
        this.summary = { ...emptySummary(), ...(data.summary || {}) };
        this.expanded = [];
      } catch (error) {
        this.rows = [];
        this.summary = emptySummary();
        this.notifyError(error);
      } finally {
        this.loading = false;
      }
    },
    async onExport() {
      this.exporting = true;
      try {
        const res = await this.$axios.get('v2/report-ar-bill-placement/export', { params: this.params() });
        const link = res.data && res.data.data && res.data.data.download_link;
        if (link) window.open(link);
      } catch (error) {
        this.notifyError(error);
      } finally {
        this.exporting = false;
      }
    },
    notifyError(error) {
      const message = (error.response && error.response.data && error.response.data.message) || 'ເກີດຂໍ້ຜິດພາດ';
      this.$store.commit('Toast_State', { value: true, color: 'error', msg: message });
    },
  },
};
</script>

<style scoped>
/* Same look as the AR collection report: the opened AR row stands out and its customers
   sit indented under it in a tinted panel with smaller text. */
::v-deep .v-data-table__expanded__row {
  background: #e3f2fd;
  font-weight: 500;
}

::v-deep .v-data-table__expanded__content {
  box-shadow: none !important;
}

.ar-bills-cell {
  padding: 4px 8px 12px 40px !important;
  background: #fafafa;
}

.ar-bills {
  border-left: 4px solid #1976d2;
  border-radius: 0 4px 4px 0;
  background: #f5f9fd;
  padding: 8px 12px;
}

.ar-bills-title {
  font-size: 12px;
  font-weight: 500;
}

.ar-bills-sub {
  font-size: 10px;
  line-height: 1.2;
}

.ar-bills-table {
  background: transparent !important;
}

.ar-bills-table ::v-deep th,
.ar-bills-table ::v-deep td {
  font-size: 11px !important;
  height: auto !important;
  padding: 4px 8px !important;
  vertical-align: top;
}

.bill-line {
  display: flex;
  gap: 8px;
  align-items: center;
  white-space: nowrap;
  line-height: 20px;
}
</style>
